import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Minus,
  Plus,
  RotateCw,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { extractErrorMessage } from "@/lib/api-errors";
import { useBackGuard } from "@/lib/scan-ui/back-guard";

/** Минимум, который галерея знает о фото; остальное — через колбэки. */
export interface LightboxPhoto {
  id: string;
  created_at: string;
}

/**
 * Полноэкранный просмотр фото (журнал приёмки, журнал отгрузки): стрелки/
 * клавиши ←→, счётчик, масштаб, поворот на 90° и скачивание (файл уходит В ТОМ
 * ПОВОРОТЕ, как на экране — повернул как надо → скачал как видишь), закрытие
 * Esc/крестик/клик по фону. Откуда брать картинку, как её подписать и назвать
 * файл — решает раздел (`loadUrl`, `title`, `caption`, `fileName`).
 * Object URL'ы кешируются только для текущего и соседних фото (остальные
 * отзываются при листании) и отзываются при размонтировании (auth — Bearer, голый <img src> получил бы 401). Фото не
 * загрузилось — текст ошибки на месте картинки, а не вечный спиннер.
 */
export function PhotoLightbox<P extends LightboxPhoto>({
  photos,
  index,
  loadUrl,
  title,
  caption,
  fileName,
  onNavigate,
  onClose,
}: {
  photos: P[];
  index: number;
  /** Картинка фото → object URL (отзывает галерея). */
  loadUrl: (photo: P) => Promise<string>;
  title: (photo: P) => string;
  /** Вторая подпись (например, кто снял); по умолчанию — время съёмки. */
  caption?: (photo: P) => string;
  /** Имя скачиваемого файла. */
  fileName: (photo: P, index: number) => string;
  onNavigate: (index: number) => void;
  onClose: () => void;
}): React.ReactElement | null {
  useBackGuard(true, onClose);
  const urlsRef = useRef<Map<string, string>>(new Map());
  const [url, setUrl] = useState("");
  const [loadError, setLoadError] = useState("");
  // Поворот — на ТЕКУЩЕЕ фото (телефонные кадры бывают лежачими);
  // при листании сбрасывается.
  const [rotation, setRotation] = useState(0);
  // Масштаб — на ТЕКУЩЕЕ фото, как и поворот. Нужен для мелкого текста в УПД:
  // «вписано в экран» читаемо не всегда, а разглядывать приходится прямо тут.
  const [zoom, setZoom] = useState(1);
  const photo = photos[index];

  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
      urls.clear();
    };
  }, []);

  useEffect(() => {
    if (!photo) return;
    setRotation(0);
    setZoom(1);
    // Полноразмерные кадры — по десятку МБ: держим только текущее и соседние
    // (листают чаще всего на шаг), остальные отзываем, иначе просмотр сотни
    // фото складывает их все в память до закрытия.
    const keep = new Set(
      [photos[index - 1], photo, photos[index + 1]]
        .filter((p): p is P => Boolean(p))
        .map((p) => p.id),
    );
    for (const [id, u] of urlsRef.current) {
      if (!keep.has(id)) {
        URL.revokeObjectURL(u);
        urlsRef.current.delete(id);
      }
    }
    const cached = urlsRef.current.get(photo.id);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    setUrl("");
    setLoadError("");
    loadUrl(photo)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        urlsRef.current.set(photo.id, u);
        setUrl(u);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(extractErrorMessage(e, "Не удалось загрузить фото"));
      });
    return () => {
      cancelled = true;
    };
    // `loadUrl` у вызывающих — новая стрелка на каждый рендер; фото определяет
    // его id, перезапрос на каждый рендер родителя не нужен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo]);

  const download = async () => {
    if (!url || !photo) return;
    const name = fileName(photo, index);
    try {
      let href = url;
      let revoke = false;
      if (rotation % 360 !== 0) {
        // Скачиваем В ТОМ ПОВОРОТЕ, как на экране: перерисовываем на canvas.
        const img = new Image();
        img.src = url;
        await img.decode();
        const odd = rotation % 180 !== 0;
        const canvas = document.createElement("canvas");
        canvas.width = odd ? img.naturalHeight : img.naturalWidth;
        canvas.height = odd ? img.naturalWidth : img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas 2d недоступен");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        const blob = await new Promise<Blob | null>((res) =>
          canvas.toBlob(res, "image/jpeg", 0.92),
        );
        if (!blob) throw new Error("не удалось собрать JPEG");
        href = URL.createObjectURL(blob);
        revoke = true;
      }
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      a.click();
      if (revoke) setTimeout(() => URL.revokeObjectURL(href), 5_000);
    } catch (e) {
      toast.error(`Не удалось скачать фото: ${(e as Error).message}`);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
      else if (e.key === "ArrowRight" && index < photos.length - 1)
        onNavigate(index + 1);
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(6, z + 0.5));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onNavigate, onClose]);

  if (!photo) return null;
  const heading = title(photo);
  const sub = caption
    ? caption(photo)
    : new Date(photo.created_at).toLocaleString("ru-RU");

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-black/95"
      onClick={onClose}
    >
      <div
        className="flex items-center gap-2 p-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="min-w-0 truncate font-medium">{heading}</span>
        <span className="shrink-0 text-sm text-white/60">
          {index + 1} / {photos.length}
        </span>
        <span className="ml-auto min-w-0 truncate text-xs text-white/60">
          {sub}
        </span>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 2) / 2))}
          disabled={zoom <= 1}
          className="flex size-10 items-center justify-center rounded-md hover:bg-white/10 disabled:opacity-40"
          aria-label="Уменьшить"
          title="Уменьшить"
        >
          <Minus className="size-5" />
        </button>
        <span className="w-10 text-center text-xs text-white/70">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(6, z + 0.5))}
          disabled={zoom >= 6}
          className="flex size-10 items-center justify-center rounded-md hover:bg-white/10 disabled:opacity-40"
          aria-label="Увеличить"
          title="Увеличить"
        >
          <Plus className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => setRotation((r) => (r + 90) % 360)}
          className="flex size-10 items-center justify-center rounded-md hover:bg-white/10"
          aria-label="Повернуть на 90°"
          title="Повернуть"
        >
          <RotateCw className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => void download()}
          disabled={!url}
          className="flex size-10 items-center justify-center rounded-md hover:bg-white/10 disabled:opacity-40"
          aria-label="Скачать фото"
          title="Скачать"
        >
          <Download className="size-5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex size-10 items-center justify-center rounded-md hover:bg-white/10"
          aria-label="Закрыть"
        >
          <X className="size-6" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        {url ? (
          <div
            className={
              "flex size-full items-center justify-center " +
              // Увеличенное фото должно ПРОКРУЧИВАТЬСЯ: без этого зум показывает
              // середину и обрезает края — то есть не решает задачу «разглядеть».
              (zoom > 1 ? "overflow-auto" : "overflow-hidden")
            }
          >
            <img
              src={url}
              alt={heading}
              className="transition-transform duration-200"
              // При повороте на 90/270° габариты меняются местами: ширину
              // ИСХОДНИКА ограничиваем высотой экрана, высоту — шириной.
              style={{
                transform: `rotate(${rotation}deg) scale(${zoom})`,
                ...(rotation % 180 === 0
                  ? { maxWidth: "100%", maxHeight: "100%" }
                  : { maxWidth: "calc(100dvh - 76px)", maxHeight: "100vw" }),
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : loadError ? (
          <div
            role="alert"
            className="flex size-full items-center justify-center p-6 text-center text-sm text-rose-300"
          >
            {loadError}
          </div>
        ) : (
          <div className="flex size-full items-center justify-center text-white/70">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}
        {index > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(index - 1);
            }}
            className="absolute left-2 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Предыдущее фото"
          >
            <ChevronLeft className="size-7" />
          </button>
        )}
        {index < photos.length - 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(index + 1);
            }}
            className="absolute right-2 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Следующее фото"
          >
            <ChevronRight className="size-7" />
          </button>
        )}
      </div>
    </div>
  );
}
